import { axiosFactory } from '../../../../client';
import {
  CoordinatorRegisteredExamResponse,
  CreateExamRegistrationRequest,
  CreateExamRegistrationResponse,
  ExamDownloadSummaryResponse,
  ExamLevelChecking,
  ExamRegistrationEligibilityRequest,
  GetWithdrawalDraftResponse,
  GroupExamWithdrawalCandidateRequest,
  GroupExamWithdrawalCandidateResponseBody,
  GroupExamWithdrawalDeleteCandidateRequest,
  GroupExamWithdrawalModalAddCandidateRequest,
  GroupExamWithdrawalModalCandidateRequest,
  GroupWithdrawal,
  GroupWithdrawalSummary,
  PutWithdrawalDetailsRequestData,
  WithdrawalSubmittedDetails,
} from '../../model';
import {
  DELETE_ADDED_CANDIDATES,
  DELETE_GROUP_REGISTRATION,
  GET_EXAM_LEVEL_BY_CODE,
  GET_EXAM_LEVELS,
  GET_REGISTERED_EXAM,
  GET_WITHDRAWAL_PDF,
  GET_WITHDRAWAL_RESULT,
  GET_WITHDRAWAL_SELECTED_REASON,
  GET_WITHDRAWAL_SUMMARY,
  GET_WITHDRAWALS,
  GET_WITHDRAWALS_SUMMARY,
  POST_ADDED_CANDIDATES,
  POST_CANDIDATES,
  POST_CHECK_EXAM_LEVELS,
  POST_CREATE_WITHDRAWAL,
  POST_SEARCH_WITHDRAWAL_CANDIDATES,
  PUT_CONFIRM_WITHDRAWAL,
  PUT_WITHDRAWAL_REASON,
} from './routes';
import { MenuItem } from '../../../../model';
import { GroupRegistrationSummaryResponse } from '../../../../../api-refactored/group-exam-registration/useGetRegistrationSummary';

const baseURL = `${
  import.meta.env.VITE_APP_API_DOMAIN
}/candidate/api/exam-registrations/coordinator`;
const groupExamRegistrationBaseURL = `${
  import.meta.env.VITE_APP_API_DOMAIN
}/candidate/api/group-exam-registrations`;

const timeout = 5000;

export const examCandidateClient = axiosFactory(baseURL, timeout);
export const groupExamRegistrationClient = axiosFactory(groupExamRegistrationBaseURL, timeout);

/** get registered exam listing
 * */
export const getRegisteredExam = () =>
  examCandidateClient.get<CoordinatorRegisteredExamResponse[]>(GET_REGISTERED_EXAM);

export const getExamLevels = () => groupExamRegistrationClient.get<MenuItem[]>(GET_EXAM_LEVELS);

export const postCheckExamLevels = (csrfToken: string, examLevelChecking: ExamLevelChecking) =>
  groupExamRegistrationClient.post<void>(csrfToken, POST_CHECK_EXAM_LEVELS, examLevelChecking);

export const getExamLevelByCode = (examTypeCode: string) =>
  groupExamRegistrationClient.get<ExamRegistrationEligibilityRequest>(GET_EXAM_LEVEL_BY_CODE, {
    examTypeCode,
  });

export const postCreateWithdrawal = (
  csrfToken: string,
  createExamRegistrationRequest: CreateExamRegistrationRequest
) =>
  groupExamRegistrationClient.post<CreateExamRegistrationResponse>(
    csrfToken,
    POST_CREATE_WITHDRAWAL,
    createExamRegistrationRequest
  );

export const getGroupWithdrawalSummary = (uuid: string) =>
  groupExamRegistrationClient.get<GroupRegistrationSummaryResponse>(GET_WITHDRAWAL_SUMMARY(uuid));

export const getWithdrawalSelectedReason = (uuid: string) =>
  groupExamRegistrationClient.get<GetWithdrawalDraftResponse>(GET_WITHDRAWAL_SELECTED_REASON(uuid));

export const putWithdrawalReason = (
  csrfToken: string,
  withdrawalDetails: PutWithdrawalDetailsRequestData
) =>
  groupExamRegistrationClient.put<CreateExamRegistrationResponse>(
    csrfToken,
    PUT_WITHDRAWAL_REASON(withdrawalDetails.payload.uuid),
    withdrawalDetails.payload
  );

export const getWithdrawals = () =>
  groupExamRegistrationClient.get<GroupWithdrawal[]>(GET_WITHDRAWALS);

export const deleteGroupRegistration = (csrfToken: string, uuid: string) =>
  groupExamRegistrationClient.delete<void>(csrfToken, DELETE_GROUP_REGISTRATION(uuid));

export const postCandidates = (
  csrfToken: string,
  payload: GroupExamWithdrawalModalCandidateRequest
) =>
  groupExamRegistrationClient.post<GroupExamWithdrawalCandidateResponseBody>(
    csrfToken,
    POST_CANDIDATES(payload.uuid, payload.apiPathParam),
    {
      q: payload.q,
      examLevel: payload.examLevel,
      examSeries: payload.examSeries,
    }
  );

export const getWithdrawalsSummary = (uuid: string) =>
  groupExamRegistrationClient.get<GroupWithdrawalSummary>(GET_WITHDRAWALS_SUMMARY(uuid));

export const putConfirmWithdrawal = (csrfToken: string, uuid: string) =>
  groupExamRegistrationClient.put<void>(csrfToken, PUT_CONFIRM_WITHDRAWAL(uuid));

export const postSearchWithdrawalCandidates = (
  csrfToken: string,
  payload: GroupExamWithdrawalCandidateRequest
) =>
  groupExamRegistrationClient.post<GroupExamWithdrawalCandidateResponseBody>(
    csrfToken,
    POST_SEARCH_WITHDRAWAL_CANDIDATES(payload.uuid, payload.apiPathParam),
    {
      q: payload.q,
    }
  );

export const postAddedCandidates = (
  csrfToken: string,
  payload: GroupExamWithdrawalModalAddCandidateRequest
) =>
  groupExamRegistrationClient.post<GroupExamWithdrawalCandidateResponseBody>(
    csrfToken,
    POST_ADDED_CANDIDATES(payload.applicationUuid),
    payload.uuidArray
  );

export const deleteAddedCandidates = (
  csrfToken: string,
  payload: GroupExamWithdrawalDeleteCandidateRequest
) =>
  groupExamRegistrationClient.delete<void>(
    csrfToken,
    DELETE_ADDED_CANDIDATES(payload.applicationUuid, payload.candidateUuid)
  );

export const getWithdrawalsResult = (uuid: string) =>
  groupExamRegistrationClient.get<WithdrawalSubmittedDetails>(GET_WITHDRAWAL_RESULT(uuid));

export const getWithdrawalPdf = (uuid: string) =>
  groupExamRegistrationClient.get<ExamDownloadSummaryResponse>(GET_WITHDRAWAL_PDF(uuid));
